/**
 * VowsEra - Main Entry Point
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';

// Providers
import { AuthProvider }     from '@context/AuthContext';
import { PlanProvider }     from '@context/PlanContext';
import { ThemeProvider }    from '@context/ThemeContext';
import { LanguageProvider } from '@context/LanguageContext';
import { CountryProvider }  from '@context/CountryContext';
import { MarketProvider }   from '@context/MarketContext';
import { ToastProvider }    from '@context/ToastContext';

// ── Realtime: Laravel Echo + Reverb (replaces Socket.io SocketProvider) ──────
import EchoProvider from '@context/EchoContext';

// Crash reporting — installed before render so errors thrown during the
// initial mount are captured too.
import { installGlobalErrorHandlers } from '@utils/errorReporter';

// i18n
import './i18n';

// Styles
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'react-toastify/dist/ReactToastify.css';
import '@styles/main.scss';

// Main App
import App from './App';
import InternalGate from './pages/InternalGate'

installGlobalErrorHandlers();

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Render application
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <MarketProvider>
          <LanguageProvider>
            <CountryProvider>
            <ThemeProvider>
              <AuthProvider>
                {/* EchoProvider connects to Reverb after auth, disconnects on logout.
                    Identical lifecycle to the old SocketProvider — all 6 realtime
                    hooks call useEcho() instead of useSocket() now. */}
                <EchoProvider>
                <PlanProvider>
                <ToastProvider>
                <InternalGate><App /></InternalGate>
                <ToastContainer
                  position="top-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="light"
                />
              </ToastProvider>
              </PlanProvider>
              </EchoProvider>
              </AuthProvider>
            </ThemeProvider>
            </CountryProvider>
          </LanguageProvider>
          </MarketProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);
